<template>
  <SearchApiEntityItemInput
    :search-result-options="searchResultOptions"
    :is-fetching-results="isFetchingResults"
    :placeholder="resolvedPlaceholder"
    :initial-value="initialLabel"
    :label="label"
    :variant="variant"
    :disabled="disabled"
    :clearable="clearable"
    :class="props.class"
    @fetch="fetchResults"
    @update-selected-item="handleUpdateSelectedItem"
  />
</template>

<script lang="ts" setup>
import { useUsersWithQuery } from '@/queries/use-users';
import SearchApiEntityItemInput from '@/components/SearchApiEntityItemInput.vue';
import { computed, ref } from 'vue';
import { useFlattenPaginatedData } from '@/hooks/use-flatten-paginated-data';
import { useI18n } from 'vue-i18n';
import type { User } from '@/entities/auth/user.entity';
import { UserRole } from '@/entities/auth/user-role.enum';

const props = defineProps<{
  allowedRoles?: UserRole[];
  placeholder?: string;
  selectedUser?: User;
  label?: string;
  variant?: 'default' | 'inline';
  disabled?: boolean;
  class?: string;
  clearable?: boolean;
}>();

const emit = defineEmits<{
  (e: 'update-user', value: User | undefined): void;
}>();

const { t } = useI18n();

const searchString = ref('');
const resolvedPlaceholder = computed(
  () =>
    props.placeholder ??
    (props.allowedRoles?.includes(UserRole.AGENCY) ? t('typeAgentName') : t('typeCustomerName')),
);

const query = computed(() => ({
  query: searchString.value,
  role: props.allowedRoles,
  limit: 20,
}));

const { data: searchResultsData, isFetching: isFetchingResults } = useUsersWithQuery(query);

const fetchResults = (search: string) => {
  searchString.value = search;
};

const searchResults = useFlattenPaginatedData(searchResultsData);

const searchResultOptions = computed(() => {
  return searchResults.value.map((user) => {
    const content = [
      user.firstName + ' ' + user.lastName + (user.company ? ', ' + user.company : ''),
    ];
    user.street && content.push(user.street);
    (user.zip || user.city) && content.push((user.zip || '-') + ' ' + (user.city ?? '-'));
    user.email && content.push(user.email);
    return {
      item: user,
      content,
    };
  });
});

const initialLabel = computed(() => {
  const u = props.selectedUser;
  if (!u) return '';
  return `${u.firstName} ${u.lastName}${u.company ? ', ' + u.company : ''}`;
});

const handleUpdateSelectedItem = (item: any) => {
  emit('update-user', item as User | undefined);
};
</script>

<i18n lang="json">
{
  "en": {
    "typeAgentName": "Type agency name",
    "typeCustomerName": "Type customer name"
  },
  "de": {
    "typeAgentName": "Vermittler-Namen eingeben",
    "typeCustomerName": "Kundenname eingeben"
  },
  "es": {
    "typeAgentName": "Escriba el nombre de la agencia",
    "typeCustomerName": "Escriba el nombre del cliente"
  }
}
</i18n>
